import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  TimeSlotTherapyCandidatesDto,
  TherapyRxTherapyCandidatesDto,
} from "../../api-client";
import { useApi } from "../../api/ApiProvider";
import { invalidateCalendarAppointmentCaches } from "../Calendar/calendar.keys";

/**
 * MD-302 — hooks for the per-slot therapy method picker.
 *
 * A prescribed umbrella term (e.g. Wärmetherapie) resolves to several billable
 * methods; these hooks read the permitted candidates for one slot and persist
 * an explicit user pick (or reset to the automatic default rule).
 */

const therapyCandidatesKey = (timeSlotId: string) =>
  ["therapyCandidates", { timeSlotId }] as const;

export const useTherapyCandidatesForTimeSlot = (timeSlotId?: string, enabled = true) => {
  const { appointments } = useApi();
  return useQuery<TimeSlotTherapyCandidatesDto>({
    queryKey: therapyCandidatesKey(timeSlotId ?? ""),
    queryFn: async () => appointments.getTherapyCandidatesForTimeSlot(timeSlotId!),
    enabled: enabled && !!timeSlotId,
    // Always fresh: the current selection and credentials drive what the
    // dialog offers, and a stale "current" would mislead.
    staleTime: 0,
    meta: { errorMessage: "Alternative Methoden konnten nicht geladen werden" },
  });
};

export const useSetTimeSlotTherapy = () => {
  const { appointments } = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      timeSlotId,
      therapyId,
    }: {
      timeSlotId: string;
      therapyId: string | null;
    }): Promise<TimeSlotTherapyCandidatesDto> =>
      appointments.setTimeSlotTherapy(timeSlotId, { therapyId }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: therapyCandidatesKey(variables.timeSlotId),
      });
      // The slot's abbreviation/duration changed — refresh every appointment
      // cache the calendar and patient views read from.
      invalidateCalendarAppointmentCaches(queryClient);
    },
    meta: {
      successMessage: "Methode aktualisiert.",
      errorMessage: "Methode konnte nicht geändert werden",
    },
  });
};

/**
 * MD-325 — the same choice, made on the prescription LINE.
 *
 * The per-slot hooks above need a slot to exist. On "Terminformat für neue
 * Rezepte" nothing is scheduled yet, so the planner picks the application
 * against the line itself, and every appointment created from it inherits the
 * choice instead of re-deriving one per booked therapist.
 */
const therapyRxCandidatesKey = (therapyRxId: string) =>
  ["therapyRxCandidates", { therapyRxId }] as const;

export const useTherapyCandidatesForTherapyRx = (therapyRxId?: string, enabled = true) => {
  const { appointments } = useApi();
  return useQuery<TherapyRxTherapyCandidatesDto>({
    queryKey: therapyRxCandidatesKey(therapyRxId ?? ""),
    queryFn: async () => appointments.getTherapyCandidatesForTherapyRx(therapyRxId!),
    enabled: enabled && !!therapyRxId,
    staleTime: 0,
    meta: { errorMessage: "Anwendungen konnten nicht geladen werden" },
  });
};

export const useSetTherapyRxTherapy = () => {
  const { appointments } = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      therapyRxId,
      therapyId,
    }: {
      therapyRxId: string;
      therapyId: string | null;
    }): Promise<TherapyRxTherapyCandidatesDto> =>
      appointments.setTherapyRxTherapy(therapyRxId, { therapyId }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: therapyRxCandidatesKey(variables.therapyRxId),
      });
      // Existing appointments are deliberately NOT re-pointed by this call, but
      // the Rx views show the line's choice, so they still need refreshing.
      invalidateCalendarAppointmentCaches(queryClient);
    },
    meta: {
      successMessage: "Anwendung für diese Verordnungszeile festgelegt.",
      errorMessage: "Anwendung konnte nicht festgelegt werden",
    },
  });
};
